import { makeStyles } from '@mui/styles';
import CustomTable from 'apollo-react/components/CustomTable';
import Grid from 'apollo-react/components/Grid';
import TableBody from 'apollo-react/components/TableBody';
import TableCell from 'apollo-react/components/TableCell';
import TableHead from 'apollo-react/components/TableHead';
import TableRow from 'apollo-react/components/TableRow';
import { isNil } from 'lodash';
import React from 'react';

import { useFormSelector } from '../../../custom-hooks';
import { getUITA, isTrue } from '../../../utils/commonUtils';
import Accordion from '../../components/Accordion';
import NewComponent from '../../components/editor/components/NewComponent';
import { FORM_MODEL_ATTRIBUTES } from '../../constants/form-manager.constants';
import DynamicField from '../../DynamicField';
import { getGridColumnsChildren } from '../../selectors/editor.selectors';
import { getEvaluatedExpression } from '../../selectors/form-manager.selectors';
import { getGridColumns, getSubGridByGridId } from '../../selectors/ssGrid.selectors';
import { equalityFn } from '../../utils/form-manager.utils';

const useStyles = makeStyles({
  root: {
    border: 'solid 1px #e9e9e9',
    boxShadow: '0 4px 16px 0 rgb(0 0 0 / 4%)',
    // width: 'fit-content',
  },
  tableWrapper: {
    display: 'flex',
    width: '100%',
    overflowX: 'auto',
  },
  panelWrapper: {
    '& div[class*="MuiAccordionSummary-root"]:hover': {
      background: 'transparent',
    },
  },
  table: {
    minHeight: 100,
    boxShadow: 'none',
  },
  addButtonContainer: {
    textAlign: 'right',
    paddingTop: '8px !important',
  },
  subGrid: {
    padding: '0 20px 20px',
    borderTop: 'solid 4px #e9e9e9',
    '& div[class*="newElementAddedFromFormModel"]': {
      minHeight: 40,
    },
  },
  tableColumn: {
    minWidth: 200,
  },
  tableCell: {
    minWidth: 200,
    verticalAlign: 'top',
    paddingBottom: 15,
  },
});

const SSEditorGrid = ({ childrenNodes, id, index, entityName }) => {
  const classes = useStyles();
  const title = useFormSelector(getEvaluatedExpression, {
    id,
    index,
    entityName,
    prop: FORM_MODEL_ATTRIBUTES.TITLE,
  });
  const columns = useFormSelector(getGridColumns, { childrenNodes }, equalityFn);
  const columnsChildren = useFormSelector(getGridColumnsChildren, { childrenNodes }, equalityFn);
  const subGridId = useFormSelector(getSubGridByGridId, { id });
  const header = useFormSelector(getEvaluatedExpression, {
    id,
    index,
    prop: FORM_MODEL_ATTRIBUTES.HEADER,
    entityName,
    defaultValue: true,
  });
  const showHeader = isTrue(header) && !isNil(title);

  return (
    <div
      className={classes.panelWrapper}
      ui-ta={getUITA('FormPart', subGridId ? 'SSComplexGrid' : 'SSGrid', entityName)}
    >
      <Accordion showHeader={showHeader} title={title}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <div className={classes.root}>
              <div className={classes.tableWrapper}>
                <CustomTable className={classes.table}>
                  <TableHead>
                    <TableRow>
                      {columns.map(({ columnKey, accessor }) => (
                        <TableCell className={classes.tableColumn} key={columnKey} variant="head">
                          <DynamicField
                            id={columnKey}
                            entityName={entityName}
                            fieldName={accessor}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow className={classes.tableRow}>
                      {columnsChildren.map((childNodes, index) => (
                        <TableCell
                          className={classes.tableCell}
                          key={childNodes[0] ?? `empty-grid-column-${columns[index].columnKey}`}
                        >
                          {childNodes.length ? (
                            childNodes.map((nodeId) => <DynamicField key={nodeId} id={nodeId} />)
                          ) : (
                            <NewComponent
                              controlParentId={columns[index].columnKey}
                              previousControlId=""
                              inTable={true}
                            />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </CustomTable>
              </div>
              {subGridId && (
                <div className={classes.subGrid}>
                  <DynamicField id={subGridId} />
                </div>
              )}
            </div>
          </Grid>
        </Grid>
      </Accordion>
    </div>
  );
};

export default React.memo(SSEditorGrid);
